import { Head, Link } from '@inertiajs/react'
import { PageLayout } from '@/components/landing/PageLayout'
import { PageHeader, SectionDivider } from '@/components/landing/PageHeader'
import {
  Shield,
  Lock,
  Key,
  Globe,
  Server,
  Eye,
  RefreshCw,
  UserCheck,
} from 'lucide-react'

const securityFeatures = [
  {
    icon: Lock,
    title: 'Encryption in Transit',
    description:
      'All API traffic is encrypted with TLS 1.3. Media streams use DTLS-SRTP encryption end-to-end via our WebRTC infrastructure.',
  },
  {
    icon: Key,
    title: 'API Key Security',
    description:
      'API secrets are hashed using bcrypt before storage. Keys are shown once on creation and can be rotated instantly via the dashboard.',
  },
  {
    icon: Globe,
    title: 'Edge Network Security',
    description:
      'Media traffic is processed on our global edge network, benefiting from DDoS protection, WAF, and network security infrastructure.',
  },
  {
    icon: Server,
    title: 'Infrastructure Isolation',
    description:
      'Multi-tenant data is isolated at the database level with scoped queries. Each organization\'s data is segregated using application-level access controls.',
  },
  {
    icon: Eye,
    title: 'Audit & Monitoring',
    description:
      'All API calls, room events, and administrative actions are logged. Real-time monitoring alerts our team to anomalous activity patterns.',
  },
  {
    icon: UserCheck,
    title: 'Authentication & Authorization',
    description:
      'Dashboard access uses session-based authentication with Devise. API access uses scoped API keys. Role-based access control governs all operations.',
  },
  {
    icon: RefreshCw,
    title: 'Key Rotation',
    description:
      'API keys can be rotated at any time with zero downtime. Old keys are invalidated immediately upon rotation to prevent unauthorized access.',
  },
  {
    icon: Shield,
    title: 'Rate Limiting & Abuse Prevention',
    description:
      'API endpoints are rate-limited to prevent abuse. Automated systems detect and block suspicious traffic patterns and brute-force attempts.',
  },
]

export default function Security() {
  return (
    <>
      <Head>
        <title>Security — KonexZero</title>
        <meta name="description" content="KonexZero's security practices: end-to-end encryption, DTLS-SRTP media protection, and responsible disclosure program." />
      </Head>
      <PageLayout>
        <div className="space-y-12">
          <PageHeader
            eyebrow="Security"
            title="Security at KonexZero"
            description="We take security seriously. Our platform is designed with defense-in-depth principles to protect your data and your users' communications."
          />

          <SectionDivider />

          <div className="grid gap-4 sm:grid-cols-2">
            {securityFeatures.map((feature) => {
              const Icon = feature.icon
              return (
                <article key={feature.title} className="surface-card surface-card-hover p-6">
                  <div className="flex items-center gap-3">
                    <div className="flex h-9 w-9 items-center justify-center rounded-md bg-foreground/[0.04] text-foreground/80">
                      <Icon className="h-4 w-4" strokeWidth={1.7} />
                    </div>
                    <h3 className="font-heading text-base font-semibold tracking-tight">
                      {feature.title}
                    </h3>
                  </div>
                  <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                    {feature.description}
                  </p>
                </article>
              )
            })}
          </div>

          <SectionDivider />

        <section className="space-y-6">
          <h2 className="font-heading text-2xl font-semibold">Our Security Practices</h2>

          <div className="space-y-6">
            <div className="space-y-2">
              <h3 className="font-medium">Data Protection</h3>
              <p className="text-muted-foreground leading-relaxed">
                All data at rest is encrypted. Database backups are encrypted and stored securely.
                We use PostgreSQL with strict access controls and parameterized queries to prevent
                SQL injection. Sensitive fields like API secrets are hashed using bcrypt with
                appropriate cost factors.
              </p>
            </div>

            <div className="space-y-2">
              <h3 className="font-medium">Media Stream Security</h3>
              <p className="text-muted-foreground leading-relaxed">
                Voice, video, and screen sharing streams are routed through our global edge
                network using DTLS-SRTP encryption. Media is not stored or recorded by default.
                TURN server credentials are generated with short TTLs to prevent relay abuse.
                ICE candidates are managed server-side to minimize exposure.
              </p>
            </div>

            <div className="space-y-2">
              <h3 className="font-medium">Application Security</h3>
              <p className="text-muted-foreground leading-relaxed">
                Our codebase undergoes regular static analysis with Brakeman for Ruby security
                issues and bundler-audit for dependency vulnerabilities. TypeScript strict mode is
                enforced on frontend code. All dependencies are monitored for known vulnerabilities
                and patched promptly.
              </p>
            </div>

            <div className="space-y-2">
              <h3 className="font-medium">Access Controls</h3>
              <p className="text-muted-foreground leading-relaxed">
                We implement the principle of least privilege across all system components.
                Organization members can only access their own organization's data. Admin operations
                require explicit super-admin role assignment. All administrative actions are
                logged for audit purposes.
              </p>
            </div>

            <div className="space-y-2">
              <h3 className="font-medium">Incident Response</h3>
              <p className="text-muted-foreground leading-relaxed">
                We maintain an incident response plan for security events. If we discover a
                security breach that affects your data, we will notify affected customers within
                72 hours with details about the incident and remediation steps.
              </p>
            </div>
          </div>
        </section>

        <SectionDivider />

        <section className="space-y-4">
          <h2 className="font-heading text-2xl font-semibold">Responsible Disclosure</h2>
          <p className="text-muted-foreground leading-relaxed">
            We welcome reports of security vulnerabilities. If you discover a potential security
            issue, please report it responsibly by emailing{' '}
            <a href="mailto:security@konexzero.com" className="text-primary hover:underline">
              security@konexzero.com
            </a>
            . We ask that you give us reasonable time to investigate and address the issue before
            making any public disclosure.
          </p>
          <p className="text-muted-foreground leading-relaxed">
            For more information about our privacy practices, see our{' '}
            <Link href="/privacy" className="text-primary hover:underline">
              Privacy Policy
            </Link>
            .
          </p>
        </section>
      </div>
    </PageLayout>
    </>
  )
}
